// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * iOS payments gate.
 *
 * Sprint 41, prompt 14.
 *
 * Apple App Store guideline 3.1.1 requires digital subscriptions sold inside
 * a native iOS app to use In-App Purchase. Until the Apple IAP product is
 * approved (`payments_enabled_native_ios=true` on the build), every Stripe-
 * powered "Upgrade" / "Plan" / "Pricing" / "Checkout" surface MUST be hidden
 * on iOS to avoid a 3.1.1 rejection.
 *
 * The flag is the same `NATIVE_IAP_ENABLED_IOS` that drives provider routing
 * on the server (see `payments/native/platform-routing.ts`). On the client it
 * is mirrored at build time as `VITE_NATIVE_IAP_ENABLED_IOS`.
 *
 * Truth table — what `arePaymentsVisibleOnPlatform()` returns:
 *
 *   web                              → features.payments
 *   ios + VITE_NATIVE_IAP_ENABLED_IOS=true  → features.payments (use IAP)
 *   ios + VITE_NATIVE_IAP_ENABLED_IOS=false → false (hide UI — 3.1.1)
 *   android                          → features.payments (Play Billing wraps)
 *
 * The web payments master switch `features.payments` (VITE_FEATURE_PAYMENTS)
 * still wins: a build with payments fully off keeps the upgrade UI hidden
 * regardless of platform.
 */

import { Capacitor } from "@capacitor/core";
import { features } from "@/lib/config";

export function isIosNativePlatform(): boolean {
	return Capacitor.getPlatform() === "ios";
}

/**
 * `true` when the iOS-native IAP integration is enabled for this build.
 * Mirrors the server flag `NATIVE_IAP_ENABLED_IOS`.
 */
export function isNativeIapEnabledIos(): boolean {
	return import.meta.env.VITE_NATIVE_IAP_ENABLED_IOS === "true";
}

/**
 * `true` when any payment / upgrade surface (pricing, checkout, "Upgrade"
 * CTA, plan comparison) should render on the current platform.
 *
 * iOS-specific behavior: when the IAP product is not yet approved
 * (VITE_NATIVE_IAP_ENABLED_IOS=false), every payment surface is hidden to
 * comply with App Store guideline 3.1.1. The plan card itself may still
 * render in read-only mode (showing the current plan) — this gate covers
 * the *purchase* CTAs only.
 */
export function arePaymentsVisibleOnPlatform(): boolean {
	if (!features.payments) return false;
	if (isIosNativePlatform() && !isNativeIapEnabledIos()) return false;
	return true;
}

/**
 * Convenience: `true` when the build is iOS native AND the payment surface
 * is hidden purely because IAP is not yet approved. Used by tests and the
 * informational banner in the plan card.
 */
export function isIosPaymentsHidden(): boolean {
	return (
		features.payments && isIosNativePlatform() && !isNativeIapEnabledIos()
	);
}
